import { supabase } from './supabaseClient';

export interface CalendarBookingSearchResult {
  id: string;
  title: string | null;
  status: string;
  booking_number: number | null;
  invoice_number: string | null;
  start_date: string | null;
  start_time: string | null;
  customer_name: string;
  customer_email: string | null;
  customer_phone: string | null;
  vehicle_labels: string[];
}

type BookingSearchRow = {
  id: string;
  title: string | null;
  status: string;
  booking_number: number | null;
  invoice_number?: string | null;
  start_date: string | null;
  start_time: string | null;
  customer: {
    first_name?: string | null;
    last_name?: string | null;
    email?: string | null;
    phone?: string | null;
  } | null;
  services?: Array<{
    vehicle: {
      make?: string | null;
      model?: string | null;
      year?: number | null;
      color?: string | null;
    } | null;
  }> | null;
};

const BOOKING_SEARCH_SELECT = `
  id,
  title,
  status,
  booking_number,
  start_date,
  start_time,
  customer:customers!bookings_customer_id_fkey(
    first_name,
    last_name,
    email,
    phone
  ),
  services:booking_services(
    vehicle:customer_vehicles(make, model, year, color)
  )
`;

const RESULT_LIMIT = 25;
const QUERY_LIMIT = 30;

function escapeIlike(value: string): string {
  return value.replace(/[%_\\]/g, (ch) => `\\${ch}`);
}

function customerName(customer: BookingSearchRow['customer']): string {
  if (!customer) return 'Unknown customer';
  return `${customer.first_name ?? ''} ${customer.last_name ?? ''}`.trim() || 'Unknown customer';
}

function vehicleLabels(services: BookingSearchRow['services']): string[] {
  const labels = new Set<string>();
  for (const service of services ?? []) {
    const v = service.vehicle;
    if (!v) continue;
    const parts = [v.year, v.make, v.model].filter(Boolean);
    if (parts.length) labels.add(parts.join(' '));
    else if (v.color) labels.add(v.color);
  }
  return [...labels];
}

function rowMatchesQuery(row: BookingSearchRow, query: string): boolean {
  const q = query.toLowerCase().trim();
  if (!q) return false;

  const customer = row.customer;
  const fullName = customerName(customer).toLowerCase();
  const title = (row.title ?? '').toLowerCase();
  const email = (customer?.email ?? '').toLowerCase();
  const phone = (customer?.phone ?? '').replace(/\D/g, '');
  const qDigits = q.replace(/\D/g, '');
  const bookingNum = row.booking_number != null ? String(row.booking_number) : '';
  const invoiceNum = (row.invoice_number ?? '').toLowerCase();

  if (title.includes(q)) return true;
  if (bookingNum.includes(q.replace(/\D/g, '')) || bookingNum.includes(q)) return true;
  if (invoiceNum && (invoiceNum.includes(q) || (qDigits && invoiceNum.replace(/\D/g, '').includes(qDigits)))) return true;
  if (fullName.includes(q)) return true;
  if (email.includes(q)) return true;
  if (qDigits && phone.includes(qDigits)) return true;

  for (const label of vehicleLabels(row.services)) {
    if (label.toLowerCase().includes(q)) return true;
  }

  return false;
}

function toSearchResult(row: BookingSearchRow): CalendarBookingSearchResult {
  const customer = row.customer;
  return {
    id: row.id,
    title: row.title,
    status: row.status,
    booking_number: row.booking_number,
    invoice_number: row.invoice_number ?? null,
    start_date: row.start_date,
    start_time: row.start_time,
    customer_name: customerName(customer),
    customer_email: customer?.email ?? null,
    customer_phone: customer?.phone ?? null,
    vehicle_labels: vehicleLabels(row.services),
  };
}

async function fetchBookingsByIds(
  businessId: string,
  bookingIds: string[]
): Promise<BookingSearchRow[]> {
  if (!bookingIds.length) return [];

  const { data, error } = await supabase
    .from('bookings')
    .select(BOOKING_SEARCH_SELECT)
    .eq('business_id', businessId)
    .eq('is_deleted', false)
    .in('id', bookingIds.slice(0, QUERY_LIMIT));

  if (error) {
    console.error('Error fetching bookings by id:', error);
    return [];
  }

  return (data ?? []) as BookingSearchRow[];
}

/** Search bookings (including cancelled); excludes is_deleted. */
export async function searchCalendarBookings(
  businessId: string,
  rawQuery: string
): Promise<CalendarBookingSearchResult[]> {
  const query = rawQuery.trim();
  if (!businessId || query.length < 2) return [];

  const escaped = escapeIlike(query);
  const pattern = `%${escaped}%`;
  const byId = new Map<string, BookingSearchRow>();

  const merge = (rows: BookingSearchRow[] | null | undefined) => {
    for (const row of rows ?? []) {
      if (!byId.has(row.id)) byId.set(row.id, row);
    }
  };

  const baseBookingQuery = () =>
    supabase
      .from('bookings')
      .select(BOOKING_SEARCH_SELECT)
      .eq('business_id', businessId)
      .eq('is_deleted', false);

  const [titleRes, customerRes] = await Promise.all([
    baseBookingQuery().ilike('title', pattern).order('start_date', { ascending: false }).limit(QUERY_LIMIT),
    supabase
      .from('customers')
      .select('id')
      .eq('business_id', businessId)
      .or(
        `first_name.ilike.${pattern},last_name.ilike.${pattern},email.ilike.${pattern},phone.ilike.${pattern}`
      )
      .limit(QUERY_LIMIT),
  ]);

  if (!titleRes.error) merge(titleRes.data as BookingSearchRow[]);
  else console.error('Calendar booking search (title):', titleRes.error);

  const numericOnly = query.replace(/\D/g, '');
  if (/^\d+$/.test(numericOnly)) {
    const num = parseInt(numericOnly, 10);
    if (!Number.isNaN(num)) {
      const { data, error } = await baseBookingQuery()
        .eq('booking_number', num)
        .limit(QUERY_LIMIT);
      if (!error) merge(data as BookingSearchRow[]);
    }
  }

  // Invoice number lookup → bookings (matches "18", "INV-18", partials)
  {
    const orParts = [`invoice_number.ilike.${pattern}`];
    if (numericOnly) orParts.push(`invoice_number.ilike.%${numericOnly}%`);
    const { data: invRows } = await supabase
      .from('invoices')
      .select('booking_id')
      .eq('business_id', businessId)
      .not('booking_id', 'is', null)
      .or(orParts.join(','))
      .limit(QUERY_LIMIT);
    const invBookingIds = (invRows ?? []).map((r: { booking_id: string }) => r.booking_id).filter(Boolean);
    if (invBookingIds.length) merge(await fetchBookingsByIds(businessId, invBookingIds));
  }

  const customerIds = new Set(
    (customerRes.data ?? []).map((c: { id: string }) => c.id)
  );

  const nameParts = query.split(/\s+/).filter(Boolean);
  if (nameParts.length >= 2) {
    const [firstPart, ...restParts] = nameParts;
    const lastPart = restParts.join(' ');
    const { data: nameMatches } = await supabase
      .from('customers')
      .select('id')
      .eq('business_id', businessId)
      .ilike('first_name', `%${escapeIlike(firstPart)}%`)
      .ilike('last_name', `%${escapeIlike(lastPart)}%`)
      .limit(QUERY_LIMIT);
    for (const c of nameMatches ?? []) customerIds.add(c.id);
  }

  if (customerIds.size > 0) {
    const { data, error } = await baseBookingQuery()
      .in('customer_id', [...customerIds])
      .order('start_date', { ascending: false })
      .limit(QUERY_LIMIT);
    if (!error) merge(data as BookingSearchRow[]);
  }

  const vehicleSelect = `
    ${BOOKING_SEARCH_SELECT},
    services:booking_services!inner(
      vehicle:customer_vehicles!inner(make, model, year, color)
    )
  `;
  const { data: vehicleBookings, error: vehicleErr } = await supabase
    .from('bookings')
    .select(vehicleSelect)
    .eq('business_id', businessId)
    .eq('is_deleted', false)
    .or(`make.ilike.${pattern},model.ilike.${pattern},color.ilike.${pattern}`, {
      referencedTable: 'booking_services.customer_vehicles',
    })
    .order('start_date', { ascending: false })
    .limit(QUERY_LIMIT);

  if (!vehicleErr) merge(vehicleBookings as BookingSearchRow[]);
  else {
    console.error('Calendar booking search (vehicle):', vehicleErr);
    const { data: serviceLinks } = await supabase
      .from('booking_services')
      .select(
        'booking_id, vehicle:customer_vehicles(make, model, year, color), booking:bookings!inner(business_id, is_deleted)'
      )
      .eq('booking.business_id', businessId)
      .eq('booking.is_deleted', false)
      .not('vehicle_id', 'is', null)
      .limit(200);

    const qLower = query.toLowerCase();
    const bookingIdsFromVehicle = new Set<string>();
    for (const row of serviceLinks ?? []) {
      const v = row.vehicle as {
        make?: string | null;
        model?: string | null;
        year?: number | null;
        color?: string | null;
      } | null;
      if (!v) continue;
      const label = [v.year, v.make, v.model, v.color]
        .filter(Boolean)
        .join(' ')
        .toLowerCase();
      if (label.includes(qLower)) bookingIdsFromVehicle.add(row.booking_id);
    }
    if (bookingIdsFromVehicle.size > 0) {
      merge(await fetchBookingsByIds(businessId, [...bookingIdsFromVehicle]));
    }
  }

  // Attach invoice numbers to all candidates (for display + matching).
  if (byId.size > 0) {
    const { data: invNums } = await supabase
      .from('invoices')
      .select('booking_id, invoice_number')
      .eq('business_id', businessId)
      .in('booking_id', [...byId.keys()]);
    for (const r of invNums ?? []) {
      const row = r.booking_id ? byId.get(r.booking_id) : undefined;
      if (row && r.invoice_number) row.invoice_number = r.invoice_number as string;
    }
  }

  const matched = [...byId.values()].filter((row) => rowMatchesQuery(row, query));

  matched.sort((a, b) => {
    const da = a.start_date ?? '';
    const db = b.start_date ?? '';
    return db.localeCompare(da);
  });

  return matched.slice(0, RESULT_LIMIT).map(toSearchResult);
}
